import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createIntroAPI,
  deleteIntroAPI,
  getIntrosAPI,
  updateIntroAPI,
  updateIntrosOrderAPI,
} from "@/apis/intro/intro-apis";
import type { IntroCreate, IntroOrderUpdate } from "@/domain/intro";
import { queryKeys } from "./keys";

export function useIntros(cityId: number | null) {
  return useQuery({
    queryKey: queryKeys.intros.list(cityId ?? 0),
    // Query is disabled when cityId is null, so queryFn won't run with fallback value
    queryFn: () => getIntrosAPI(cityId ?? 0),
    enabled: cityId !== null,
  });
}

export function useCreateIntro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cityId, dto }: { cityId: number; dto: IntroCreate }) =>
      createIntroAPI(cityId, dto),
    onSuccess: (_, { cityId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.intros.list(cityId),
      });
    },
  });
}

export function useUpdateIntro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: Partial<IntroCreate> }) =>
      updateIntroAPI(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.intros.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useUpdateIntrosOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cityId, dto }: { cityId: number; dto: IntroOrderUpdate }) =>
      updateIntrosOrderAPI(cityId, dto),
    onSuccess: (_, { cityId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.intros.list(cityId),
      });
    },
  });
}

export function useDeleteIntro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteIntroAPI(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.intros.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}
